import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDemandDto,
  CreateDemandResponseDto,
  GetAllDemandsDto,
  GetDemandByIdResponseDto,
  GetDemandsResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { demandsQueryKeys } from '@/lib/query-keys/demands';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useDemandsApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/demands`;
  const defaultLimit = 25;

  const getDemands = async (params: GetAllDemandsDto): Promise<GetDemandsResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}`, { params });
    return data;
  };

  const getDemand = async (id: number): Promise<GetDemandByIdResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`);
    return data;
  };

  const createDemand = async (demand: CreateDemandDto): Promise<CreateDemandResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}`, demand);
    return data;
  };

  const deleteDemand = async (id: number) => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return { data };
  };

  const useGetAll = (params: GetAllDemandsDto = {}) => {
    const limit = params.limit ?? defaultLimit;
    const normalizedParams = { ...params, limit };
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: demandsQueryKeys.list(normalizedParams),
      queryFn: ({ pageParam }) => getDemands({ ...params, limit, offset: pageParam * limit }),
      getNextPageParam: (lastPage) =>
        lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined,
    });
  };

  const useGetDemand = (id: number) =>
    useQuery({
      queryKey: demandsQueryKeys.detail(id),
      queryFn: () => getDemand(id),
      enabled: id > 0,
    });

  const queryClient = useQueryClient();

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createDemand,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: demandsQueryKeys.all() }),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemand,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: demandsQueryKeys.all() }),
    });

  return {
    getDemands,
    useGetAll,
    useGetDemand,
    useCreate,
    useDelete,
  };
};
